import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Plus, Minus, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [addedToCart, setAddedToCart] = useState({});
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
      const initialQuantities = {};
      response.data.forEach(p => { initialQuantities[p.id] = 1; });
      setQuantities(initialQuantities);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (productId, delta) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(1, (prev[productId] || 1) + delta)
    }));
  };

  const handleAddToCart = (product) => {
    addToCart(product, quantities[product.id] || 1);
    setAddedToCart(prev => ({ ...prev, [product.id]: true }));
    setTimeout(() => {
      setAddedToCart(prev => ({ ...prev, [product.id]: false }));
    }, 2000);
  };

  if (loading) {
    return (
      <section id="products" className="py-24 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="bg-[#E3DBD1] h-64 rounded-sm mb-4"></div>
                <div className="bg-[#E3DBD1] h-6 w-3/4 rounded mb-2"></div>
                <div className="bg-[#E3DBD1] h-4 w-1/2 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const chocolateSpice = products.filter(p => p.category === 'chocolate-spice');
  const cuccidati = products.filter(p => p.category === 'cuccidati');

  const ProductCard = ({ product, index }) => (
    <motion.div
      key={product.id}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Card 
        className="product-card group bg-white border border-[#E3DBD1] rounded-sm overflow-hidden hover-lift"
        data-testid={`product-card-${product.id}`}
      >
        <div className="relative overflow-hidden aspect-square">
          <img
            src={product.image}
            alt={product.name}
            className="product-card-image w-full h-full object-cover"
          />
          <div className="absolute top-4 right-4 bg-[#7B3E19] text-white px-3 py-1 rounded-sm text-sm font-medium">
            {product.quantity} cookies
          </div>
        </div>
        <CardContent className="p-6">
          <h3 className="font-heading text-xl font-semibold text-[#2A1A14] mb-2" data-testid={`product-name-${product.id}`}>
            {product.name.replace(' - ', '\n').split('\n')[0]}
          </h3>
          <p className="text-[#786C65] text-sm mb-4 line-clamp-2">
            {product.description}
          </p>
          <div className="flex items-center justify-between mb-4">
            <span className="text-2xl font-heading font-semibold text-[#7B3E19]" data-testid={`product-price-${product.id}`}>
              ${product.price.toFixed(2)}
            </span>
            <div className="flex items-center gap-2 border border-[#E3DBD1] rounded-sm">
              <button
                onClick={() => handleQuantityChange(product.id, -1)}
                className="quantity-btn p-2 hover:bg-[#EAE3D9]"
                disabled={quantities[product.id] <= 1}
                data-testid={`quantity-decrease-${product.id}`}
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center font-medium" data-testid={`quantity-value-${product.id}`}>
                {quantities[product.id] || 1}
              </span>
              <button
                onClick={() => handleQuantityChange(product.id, 1)}
                className="quantity-btn p-2 hover:bg-[#EAE3D9]"
                data-testid={`quantity-increase-${product.id}`}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          <Button
            onClick={() => handleAddToCart(product)}
            className={`w-full rounded-sm transition-all duration-200 ${
              addedToCart[product.id]
                ? 'bg-green-600 hover:bg-green-600'
                : 'bg-[#7B3E19] hover:bg-[#633113]'
            } text-white`}
            data-testid={`add-to-cart-${product.id}`}
          >
            {addedToCart[product.id] ? (
              <>
                <Check className="w-5 h-5 mr-2" />
                Added!
              </>
            ) : (
              <>
                <ShoppingBag className="w-5 h-5 mr-2" />
                Add to Cart
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <section id="products" className="py-24 bg-[#FAF9F6]" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Sicilian Chocolate Spice Cookies */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-sm uppercase tracking-[0.2em] text-[#786C65] mb-4">
            Our Products
          </p>
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-[#2A1A14]" data-testid="products-title">
            Sicilian Chocolate Spice Cookies
          </h2>
          <p className="text-[#7B3E19] font-heading text-lg md:text-xl font-medium mt-3">
            Now Also Available: Cuccidati (Sicilian Fig Cookies)!
          </p>
        </motion.div>

        <div className="bg-[#EAE3D9] border border-[#E3DBD1] rounded-sm p-4 mb-12 text-center" data-testid="allergen-disclaimer-chocolate">
          <p className="text-sm text-[#2A1A14] mb-3">
            <span className="font-semibold">Allergen Notice:</span> Our products contain dairy and may also contain or have been prepared alongside nuts and/or nut oils. 
            Please consume responsibly if you have food allergies.
          </p>
          <p className="text-sm text-[#2A1A14]">
            <span className="font-semibold">Dough Ingredients:</span> baking cocoa, flour, baking powder, sugar, dark brown sugar, pink Himalayan sea salt, salted butter, grape jelly, egg yolks, whole vitamin D milk, vanilla extract, cinnamon, ground clove
          </p>
          <p className="text-sm text-[#2A1A14] mt-2">
            <span className="font-semibold">Icing Ingredients:</span> powdered sugar, salted butter, vanilla
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 mb-24">
          {chocolateSpice.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>

        {/* Cuccidati Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
          id="cuccidati"
        >
          <p className="text-sm uppercase tracking-[0.2em] text-[#786C65] mb-4">
            New Arrival
          </p>
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-[#2A1A14]" data-testid="cuccidati-title">
            Cuccidati (Sicilian Fig Cookies)
          </h2>
          <p className="text-[#786C65] mt-4 max-w-2xl mx-auto">
            A beloved Sicilian tradition! Our Cuccidati are tender pastry pockets filled with a rich fig and nut mixture, 
            topped with a sweet glaze and colorful sprinkles.
          </p>
        </motion.div>

        <div className="bg-[#EAE3D9] border border-[#E3DBD1] rounded-sm p-4 mb-12 text-center" data-testid="allergen-disclaimer-cuccidati">
          <p className="text-sm text-[#2A1A14] mb-3">
            <span className="font-semibold">Allergen Notice:</span> Our Cuccidati contain dairy and nuts. 
            Please consume responsibly if you have food allergies.
          </p>
          <p className="text-sm text-[#2A1A14]">
            <span className="font-semibold">Dough Ingredients:</span> flour, sugar, baking powder, eggs, salted butter
          </p>
          <p className="text-sm text-[#2A1A14] mt-2">
            <span className="font-semibold">Filling Ingredients:</span> sun dried figs, dates, walnuts, black pepper, cinnamon, honey
          </p>
          <p className="text-sm text-[#2A1A14] mt-2">
            <span className="font-semibold">Icing Ingredients:</span> powdered sugar, salted butter, vanilla, sprinkles
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {cuccidati.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
